import { defineStore } from 'pinia';
import api from '@/venit/components/cortona-venit/services/api';
import { v4 as uuidv4 } from 'uuid';
import { useAccountStore } from './account';

interface Toast {
  type: string;
  visible: boolean;
  message: string;
  timeout: number;
  copyText: string | null;
}

interface CartItem {
  id: string | null;
  partNumber: string;
  quantity: number;
  [key: string]: any;
}

interface CortonaViewerState {
  toast: Toast;
  modelHistory: any[];
  currentModel: any | null;
  cart: {
    items: CartItem[];
  };
}

export const useCortonaViewerStore = defineStore('cortonaViewer', {
  state: (): CortonaViewerState => ({
    toast: {
      type: 'success',
      visible: false,
      message: '',
      timeout: 2000,
      copyText: null,
    },
    modelHistory: [],
    currentModel: null,
    cart: {
      items: [],
    },
  }),

  getters: {
    cartItems: (state) => state.cart.items,
  },

  actions: {
    showToast(toast: { message: string; type?: string; timeout?: number; copyText?: string }) {
      this.toast.message = toast.message;
      this.toast.type = toast.type || 'success';
      this.toast.timeout = toast.timeout || 2000;
      this.toast.copyText = toast.copyText ?? null;
      this.toast.visible = true;
    },

    hideToast() {
      this.toast.visible = false;
    },

    navigateToModel(model: any, reset: boolean = false) {
      if (reset) {
        this.modelHistory = [];
        this.currentModel = null;
      }

      if (this.currentModel) {
        this.modelHistory.push(this.currentModel);
      }
      this.currentModel = model;
    },

    goBackToPreviousModel() {
      if (this.modelHistory.length > 0) {
        this.currentModel = this.modelHistory.pop();
      }
    },

    setCart(items: CartItem[]) {
      this.cart.items = items;
    },

    async transferLocalCart() {
      const accountStore = useAccountStore();
      let localCart: CartItem[] = JSON.parse(localStorage.getItem('cart') || '[]');
      if (localCart.length > 0) {
        localCart.forEach(it => (it.id = null));
        const response = await api.addCartItems(localCart);
        localStorage.removeItem('cart');
        this.setCart(response.items);
      }
    },

    async loadCart() {
      const accountStore = useAccountStore();
      let items: CartItem[] = [];
      if (accountStore.authenticated) {
        const response = await api.userCart();
        items = response.items;
      } else {
        items = JSON.parse(localStorage.getItem('cart') || '[]');
      }
      this.setCart(items);
      return items;
    },

    async updateItem(item: CartItem) {
      const accountStore = useAccountStore();
      let items = this.cart.items;
      if (accountStore.authenticated) {
        const response = await api.updateCart(item);
        items = response.items;
      } else {
        localStorage.setItem('cart', JSON.stringify(items));
      }
      this.setCart(items);
    },

    async addToCart(item: CartItem) {
      const accountStore = useAccountStore();
      let items = this.cart.items;
      const existing = items.findIndex(i => i.partNumber === item.partNumber);

      if (accountStore.authenticated) {
        let response;
        if (existing < 0) {
          response = await api.updateCart(item);
        } else {
          if (items[existing].quantity < 99) {
            items[existing].quantity += item.quantity;
          }
          response = await api.updateCart(items[existing]);
        }
        items = response.items;
      } else {
        if (existing < 0) {
          item.id = uuidv4();
          items.push(item);
        } else if (items[existing].quantity < 99) {
          items[existing].quantity += item.quantity;
        }
        localStorage.setItem('cart', JSON.stringify(items));
      }
      this.setCart(items);
      return existing;
    },

    async removeFromCart(id: string) {
      const accountStore = useAccountStore();
      let items = this.cart.items;
      if (accountStore.authenticated) {
        const response = await api.removeFromCart(id);
        items = response.items;
      } else {
        items = items.filter(it => it.id !== id);
        localStorage.setItem('cart', JSON.stringify(items));
      }
      this.setCart(items);
    },

    async refresh(router: any) {
      const accountStore = useAccountStore();
      if (!accountStore.logon) return;
      if (!accountStore.userIdentity) {
        await accountStore.loadUser();
      }
      // refreshToken would need to be implemented if needed
    },
  },
});
